import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Rise } from "@/components/humble/AnimatedText";
import ChapterMark from "@/components/humble/ChapterMark";
import { HUMBLE } from "@/constants/testIds";

// Same-origin Cloudflare Pages Function (functions/api/waitlist.js) — no cross-origin backend.
const API = "/api";

// Cloudflare Turnstile site key (public). When set, the form requires a token; the backend verifies
// it (TURNSTILE_SECRET). When unset, Turnstile is skipped and honeypot + KV rate-limit still apply.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const BENEFITS = [
  "Every animal on the place, on one page.",
  "Health and treatments the whole family can log.",
  "Breeding, hatchings, kiddings — with warm-up reminders before due dates.",
  "Morning and evening chores, checked off together.",
  "Works out in the barn with no signal — it syncs when you're back.",
  "Free while we're building. Save your seat now, and keep it.",
];

export default function Waitlist() {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot \u2014 real people leave this blank
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [msg, setMsg] = useState("");
  const [token, setToken] = useState(""); // Turnstile token
  const widgetRef = useRef(null);
  const widgetId = useRef(null);

  // Render the Turnstile widget once its script is ready. Managed mode resolves invisibly for most
  // visitors, handing us a token without any interaction.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    let poll;
    const render = () => {
      if (!window.turnstile || !widgetRef.current || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(widgetRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        callback: (t) => setToken(t),
        "expired-callback": () => setToken(""),
        "error-callback": () => setToken(""),
      });
    };
    if (window.turnstile) render();
    else poll = setInterval(() => {
      if (window.turnstile) { clearInterval(poll); render(); }
    }, 200);
    return () => { if (poll) clearInterval(poll); };
  }, []);

  const resetTurnstile = () => {
    setToken("");
    if (window.turnstile && widgetId.current !== null) {
      window.turnstile.reset(widgetId.current);
    }
  };

  async function onSubmit(e) {
    e.preventDefault();
    const clean = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(clean)) {
      setStatus("error");
      setMsg("That email doesn\u2019t look quite right. Mind checking it?");
      return;
    }
    if (TURNSTILE_SITE_KEY && !token) {
      setStatus("error");
      setMsg("Just a moment \u2014 the quick \u201cnot a robot\u201d check is still finishing.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/waitlist`, { email: clean, company, turnstileToken: token });
      setStatus("success");
      setMsg("You\u2019re on the list. We\u2019ll write when the barn door opens.");
      setEmail("");
      resetTurnstile();
    } catch (err) {
      setStatus("error");
      const code = err?.response?.status;
      setMsg(
        code === 429
          ? "That\u2019s a few tries in a row \u2014 give it a minute and try again."
          : "Something went sideways. Give it another try in a moment.",
      );
      resetTurnstile();
    }
  }

  return (
    <section
      id="waitlist"
      data-testid={HUMBLE.waitlistSection}
      className="relative isolate overflow-hidden bg-earth text-cream"
    >
      <ChapterMark numeral="IV" label="Save me a seat" color="hsl(var(--cream))" align="right" />

      <div className="relative z-[1] mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-16 px-6 py-28 md:px-12 md:py-40 lg:grid-cols-12 lg:gap-24">
        <div className="lg:col-span-6">
          <Rise>
            <div className="mb-6 flex items-center gap-3 font-body uppercase text-[11px] tracking-[0.42em] text-cream/60">
              <span className="inline-block h-px w-8 bg-gold/70" />
              Chapter IV — a warm invitation
            </div>
          </Rise>
          <Rise delay={0.05}>
            <h2 className="font-display text-4xl leading-[1.05] tracking-tight text-cream md:text-5xl lg:text-[64px]">
              Stop losing track of the homestead you love.
            </h2>
          </Rise>
          <Rise delay={0.12}>
            <p className="mt-6 max-w-xl font-body text-lg text-cream/80">
              This is the tool we wished we&rsquo;d had, built slowly and on
              purpose. If it sounds like your kind of quiet, there&rsquo;s a
              seat here with your name on it.
            </p>
          </Rise>

          <Rise delay={0.2}>
            <ul className="mt-8 space-y-3 font-body text-[15px] text-cream/85">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-[10px] inline-block h-[6px] w-[6px] rounded-full bg-gold"
                  />
                  {b}
                </li>
              ))}
            </ul>
          </Rise>
        </div>

        <div className="lg:col-span-6 lg:pl-6">
          <Rise delay={0.1}>
            <form
              data-testid={HUMBLE.waitlistForm}
              onSubmit={onSubmit}
              className="relative rounded-[10px] border border-cream/12 bg-cream/[0.03] p-6 backdrop-blur-sm md:p-10"
            >
              {/* Honeypot — hidden from people, bots fill it. Off-screen, not tabbable. */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                }}
              >
                <label htmlFor="wl-company">Company</label>
                <input
                  id="wl-company"
                  type="text"
                  name="company"
                  tabIndex={-1}
                  autoComplete="off"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </div>

              <label
                htmlFor="wl-email"
                className="block font-body uppercase text-[11px] tracking-[0.4em] text-cream/60"
              >
                Your email
              </label>
              <input
                id="wl-email"
                type="email"
                required
                data-testid={HUMBLE.waitlistEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourfarm.com"
                className="mt-3 w-full bg-transparent font-display text-2xl md:text-3xl italic text-cream placeholder:text-cream/25 focus:outline-none border-b border-cream/25 pb-3 focus:border-gold transition-colors"
              />

              {TURNSTILE_SITE_KEY && (
                <div ref={widgetRef} className="mt-6" aria-label="Verification" />
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid={HUMBLE.waitlistSubmit}
                className="group mt-8 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-body text-[15px] font-medium text-earth transition hover:bg-[hsl(var(--gold-deep))] hover:text-cream disabled:opacity-60"
              >
                {status === "loading" ? "Saving your seat\u2026" : "Save me a seat"}
                <span className="inline-block transition-transform group-hover:translate-x-1">
                  &rarr;
                </span>
              </button>

              <p className="mt-6 max-w-md font-hand text-xl text-cream/75">
                We&rsquo;ll write when the barn door opens — one warm note,
                then nothing you didn&rsquo;t ask for.
              </p>

              {status === "success" && (
                <p
                  data-testid={HUMBLE.waitlistSuccess}
                  className="mt-5 font-body text-sm text-gold"
                  role="status"
                >
                  {msg}
                </p>
              )}
              {status === "error" && (
                <p
                  data-testid={HUMBLE.waitlistError}
                  className="mt-5 font-body text-sm text-clay"
                  role="alert"
                >
                  {msg}
                </p>
              )}
            </form>
          </Rise>
        </div>
      </div>
    </section>
  );
}
